import { defineStore } from 'pinia'
import { STORE_ID } from '~/constants/store'
import type { TrackingAttribution, UtmCodes, TrackState as State } from '~/types/track'
import { TRACKING_ATTRIBUTION_INACTIVITY_TIMEOUT_MS } from '~/utils/trackingAttribution'

const initialState = (): State => ({
  utmCodes: undefined,
  attribution: undefined,
  user: undefined,
  device: undefined,
  advertisingIdentifiers: undefined,
  app: undefined
})

/**
 * @description Store for user preferences - saved in local storage.
 */
const useTrackStore = defineStore(STORE_ID.TRACK, {
  state: initialState,

  getters: {},

  actions: {
    setUtmCodes(utmCodes: UtmCodes): void {
      try {
        this.setAttribution(utmCodes)
      } catch (error) {
        console.warn(error)
      }
    },
    setAttribution(attribution: TrackingAttribution): void {
      try {
        const capturedAt = attribution.capturedAt ?? Date.now()
        // A new tagged entry is one complete attribution bundle. Do not retain
        // fields from an earlier campaign when the new link omits them.
        this.attribution = {
          ...attribution,
          capturedAt,
          lastActivityAt: attribution.lastActivityAt ?? capturedAt
        }
        this.utmCodes = attribution.source
          ? {
              source: attribution.source,
              medium: attribution.medium ?? '',
              campaign: attribution.campaign ?? '',
              content: attribution.content ?? '',
              term: attribution.term ?? ''
            }
          : undefined
      } catch (error) {
        console.warn(error)
      }
    },
    clearAttribution(reason = 'cleared'): void {
      if (this.attribution) {
        console.info('Tracking attribution cleared', {
          reason,
          source: this.attribution.source,
          ageMs: Date.now() - (this.attribution.capturedAt ?? Date.now())
        })
      }
      this.attribution = undefined
      this.utmCodes = undefined
    },
    getActiveAttribution(touchActivity = true, now = Date.now()): TrackingAttribution | undefined {
      if (!this.attribution) return undefined

      const lastActivityAt = this.attribution.lastActivityAt ?? this.attribution.capturedAt
      if (!lastActivityAt || now - lastActivityAt >= TRACKING_ATTRIBUTION_INACTIVITY_TIMEOUT_MS) {
        this.clearAttribution('expired')
        return undefined
      }

      if (touchActivity) {
        this.attribution.lastActivityAt = now
      }
      return this.attribution
    },
    updateUser(user: State['user']): void {
      try {
        // update the user with only the new properties
        this.user = { ...this.user, ...user, lastUpdated: Date.now() }
      } catch (error) {
        console.warn(error)
      }
    },
    setUser(user: State['user']): void {
      try {
        this.user = user
        if (this.user) {
          this.user.lastUpdated = Date.now()
        }
      } catch (error) {
        console.warn(error)
      }
    },
    setMetaClickId(clickId: string): void {
      try {
        if (this.browser && this.browser.metaClickId !== clickId) {
          this.browser = {
            ...this.browser,
            metaClickId: clickId,
            fbc: `fb.2.${Date.now()}.${clickId}`
          }
        }
      } catch (error) {
        console.warn(error)
      }
    },
    setTikTokClickId(clickId: string): void {
      try {
        this.browser = { ...this.browser, ttc: clickId }
      } catch (error) {
        console.warn(error)
      }
    },
    updateBrowser(browser: State['browser']): void {
      try {
        // update the browser with only the new properties
        this.browser = { ...this.browser, ...browser }
      } catch (error) {
        console.warn(error)
      }
    },
    setBrowser(browser: State['browser']): void {
      try {
        this.browser = browser
      } catch (error) {
        console.warn(error)
      }
    },
    updateDevice(device: State['device']): void {
      try {
        // update the device with only the new properties
        this.device = { ...this.device, ...device, lastUpdated: Date.now() }
      } catch (error) {
        console.warn(error)
      }
    },
    setDevice(device: State['device']): void {
      try {
        this.device = device
        if (this.device) {
          this.device.lastUpdated = Date.now()
        }
      } catch (error) {
        console.warn(error)
      }
    },
    updateAdvertisingIdentifiers(advertisingIdentifiers: State['advertisingIdentifiers']): void {
      try {
        // update the permissions with only the new properties
        this.advertisingIdentifiers = {
          ...this.advertisingIdentifiers,
          ...advertisingIdentifiers,
          lastUpdated: Date.now()
        }
      } catch (error) {
        console.warn(error)
      }
    },
    setAdvertisingIdentifiers(advertisingIdentifiers: State['advertisingIdentifiers']): void {
      try {
        this.advertisingIdentifiers = advertisingIdentifiers
        if (this.advertisingIdentifiers) {
          this.advertisingIdentifiers.lastUpdated = Date.now()
        }
      } catch (error) {
        console.warn(error)
      }
    },
    updateApp(app: State['app']): void {
      try {
        // update the app with only the new properties
        this.app = { ...this.app, ...app, lastUpdated: Date.now() }
      } catch (error) {
        console.warn(error)
      }
    },
    setApp(app: State['app']): void {
      try {
        this.app = app
        if (this.app) {
          this.app.lastUpdated = Date.now()
        }
      } catch (error) {
        console.warn(error)
      }
    }
  },
  // Do not persist utm_codes they are only relevant for the current session
  // This may be reviewed in the future as part of the social tracking feature
  // Persist the browser details as click id
  persist: { pick: ['browser'] }
})

export default useTrackStore
export { initialState }
